import React, { useState, useEffect } from "react";
import {
    Box,
    Typography,
    Stack,
    Chip,
    Divider,
    Dialog,
    DialogTitle,
    DialogContent,
    IconButton,
    CircularProgress,
    Avatar,
    ButtonBase,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DirectionsCarIcon from "@mui/icons-material/DirectionsCar";
import PersonIcon from "@mui/icons-material/Person";
import ConfirmationNumberIcon from "@mui/icons-material/ConfirmationNumber";
import PaletteIcon from "@mui/icons-material/Palette";
import TagIcon from "@mui/icons-material/Tag";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import BuildIcon from "@mui/icons-material/Build";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import VehicleService from "../../services/Vehicle.service";

function InfoRow({ icon, label, value }) {
    if (!value && value !== 0) return null;
    return (
        <Stack direction="row" spacing={1.5} alignItems="flex-start" sx={{ mb: 1.25 }}>
            <Box sx={{ color: "secondary.main", mt: 0.2, flexShrink: 0 }}>{icon}</Box>
            <Box>
                <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontFamily: "Montserrat, sans-serif", fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.4 }}
                >
                    {label}
                </Typography>
                <Typography variant="body2" sx={{ fontFamily: "Montserrat, sans-serif" }}>
                    {value}
                </Typography>
            </Box>
        </Stack>
    );
}

function formatDate(dateStr) {
    if (!dateStr) return "";
    const d = new Date(dateStr + "T00:00:00");
    return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

export default function VehicleDetailsDialog({ vehicleSegment, onClose }) {
    const open = Boolean(vehicleSegment);
    const [vehicle, setVehicle] = useState(null);
    const [loading, setLoading] = useState(false);

    const [issuesOpen, setIssuesOpen] = useState(false);
    const [openIssues, setOpenIssues] = useState([]);
    const [issuesLoading, setIssuesLoading] = useState(false);

    const [inspectionsOpen, setInspectionsOpen] = useState(false);
    const [expiredInspections, setExpiredInspections] = useState([]);
    const [inspectionsLoading, setInspectionsLoading] = useState(false);

    const vehicleId = vehicleSegment?.vehicle || vehicleSegment?.vehicle_detail?.id;

    useEffect(() => {
        if (!vehicleId) {
            setVehicle(null);
            setIssuesOpen(false);
            setOpenIssues([]);
            setInspectionsOpen(false);
            setExpiredInspections([]);
            return;
        }
        setLoading(true);
        VehicleService.getVehicle(vehicleId)
            .then((res) => setVehicle(res.data))
            .catch(() => setVehicle(null))
            .finally(() => setLoading(false));
    }, [vehicleId]);

    const handleToggleIssues = () => {
        const next = !issuesOpen;
        setIssuesOpen(next);
        if (next && openIssues.length === 0) {
            setIssuesLoading(true);
            VehicleService.listIssues({ vehicle: vehicleId })
                .then((res) => {
                    const all = res.data || [];
                    setOpenIssues(all.filter((i) => !i.is_resolved));
                })
                .catch(() => setOpenIssues([]))
                .finally(() => setIssuesLoading(false));
        }
    };

    const handleToggleInspections = () => {
        const next = !inspectionsOpen;
        setInspectionsOpen(next);
        if (next && expiredInspections.length === 0) {
            setInspectionsLoading(true);
            VehicleService.listInspections({ vehicle: vehicleId })
                .then((res) => {
                    const today = new Date();
                    today.setHours(0, 0, 0, 0);
                    const all = res.data || [];
                    setExpiredInspections(
                        all.filter((i) => i.expiration_date && new Date(i.expiration_date) < today),
                    );
                })
                .catch(() => setExpiredInspections([]))
                .finally(() => setInspectionsLoading(false));
        }
    };

    const detail = vehicleSegment?.vehicle_detail || {};
    const nickname = vehicle?.nickname || detail.nickname || "";
    const year = vehicle?.year || detail.year || "";
    const make = vehicle?.make || detail.make || "";
    const model = vehicle?.model || detail.model || "";
    const makeModelYear = [year, make, model].filter(Boolean).join(" ");

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullWidth
            maxWidth="xs"
            PaperProps={{ sx: { borderRadius: 3 } }}
        >
            <DialogTitle sx={{ pb: 0, pr: 1 }}>
                <Stack direction="row" alignItems="flex-start" justifyContent="space-between">
                    <Stack direction="row" spacing={1.5} alignItems="center">
                        <Avatar sx={{ width: 44, height: 44, bgcolor: "secondary.main" }}>
                            <DirectionsCarIcon sx={{ fontSize: "1.2rem" }} />
                        </Avatar>
                        <Box>
                            <Typography
                                variant="h6"
                                sx={{ fontFamily: "Montserrat, sans-serif", fontWeight: 700, lineHeight: 1.2, fontSize: "1rem" }}
                            >
                                {nickname}
                            </Typography>
                            {makeModelYear && (
                                <Typography variant="caption" color="text.secondary" sx={{ fontFamily: "Montserrat, sans-serif" }}>
                                    {makeModelYear}
                                </Typography>
                            )}
                        </Box>
                    </Stack>
                    <IconButton onClick={onClose} size="small">
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </Stack>

                {/* Segment badges */}
                <Stack direction="row" spacing={0.75} sx={{ mt: 1 }} flexWrap="wrap">
                    {vehicleSegment?.is_rental && (
                        <Chip label="Rental" size="small" color="secondary" sx={{ fontFamily: "Montserrat, sans-serif", fontWeight: 700 }} />
                    )}
                    {vehicleSegment?.is_own && (
                        <Chip label="Company Vehicle" size="small" color="primary" sx={{ fontFamily: "Montserrat, sans-serif", fontWeight: 700 }} />
                    )}
                    {!vehicleSegment?.is_rental && !vehicleSegment?.is_own && (
                        <Chip label="Vehicle" size="small" variant="outlined" sx={{ fontFamily: "Montserrat, sans-serif", fontWeight: 700 }} />
                    )}
                </Stack>
            </DialogTitle>

            <Divider sx={{ mt: 1.5 }} />

            <DialogContent sx={{ pt: 2 }}>
                {loading ? (
                    <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                        <CircularProgress size={28} color="secondary" />
                    </Box>
                ) : (
                    <>
                        <InfoRow icon={<PaletteIcon fontSize="small" />} label="Color" value={vehicle?.color} />
                        <InfoRow icon={<ConfirmationNumberIcon fontSize="small" />} label="License Plate" value={vehicle?.license_plate} />
                        <InfoRow icon={<TagIcon fontSize="small" />} label="VIN" value={vehicle?.vin_number} />
                        <InfoRow icon={<TagIcon fontSize="small" />} label="Toll Tag" value={vehicle?.toll_tag} />
                        <InfoRow
                            icon={<PersonIcon fontSize="small" />}
                            label="Assigned To"
                            value={vehicle?.assignment_detail?.full_name}
                        />

                        {/* Open issues / alerts */}
                        {(vehicle?.open_issues_count > 0 || vehicle?.expired_inspections_count > 0) && (
                            <>
                                <Divider sx={{ my: 1.5 }} />
                                <Stack spacing={0.5}>

                                    {/* ── Open issues ── */}
                                    {vehicle?.open_issues_count > 0 && (
                                        <>
                                            <ButtonBase
                                                onClick={handleToggleIssues}
                                                sx={{
                                                    display: "flex",
                                                    width: "100%",
                                                    alignItems: "center",
                                                    gap: 1,
                                                    borderRadius: 1,
                                                    px: 0.5,
                                                    py: 0.5,
                                                    "&:hover": { bgcolor: "action.hover" },
                                                }}
                                            >
                                                <WarningAmberIcon fontSize="small" color="error" />
                                                <Typography variant="body2" color="error" sx={{ fontFamily: "Montserrat, sans-serif", fontWeight: 600, flex: 1, textAlign: "left" }}>
                                                    {vehicle.open_issues_count} open {vehicle.open_issues_count === 1 ? "issue" : "issues"}
                                                </Typography>
                                                <ExpandMoreIcon
                                                    fontSize="small"
                                                    sx={{
                                                        color: "error.main",
                                                        transition: "transform 0.2s",
                                                        transform: issuesOpen ? "rotate(180deg)" : "rotate(0deg)",
                                                    }}
                                                />
                                            </ButtonBase>
                                            <Box sx={{ overflow: "hidden", opacity: issuesOpen ? 1 : 0, transform: issuesOpen ? "translateY(0)" : "translateY(-4px)", transition: "opacity 0.15s ease, transform 0.15s ease", pointerEvents: issuesOpen ? "auto" : "none", display: issuesOpen ? undefined : "none" }}>
                                                <Stack spacing={0.75} sx={{ pl: 1, pb: 0.5 }}>
                                                    {issuesLoading ? (
                                                        <CircularProgress size={16} color="error" sx={{ ml: 1 }} />
                                                    ) : openIssues.length === 0 ? (
                                                        <Typography variant="caption" color="text.secondary" sx={{ fontFamily: "Montserrat, sans-serif", pl: 0.5 }}>
                                                            No details available.
                                                        </Typography>
                                                    ) : (
                                                        openIssues.map((issue) => (
                                                            <Box
                                                                key={issue.id}
                                                                sx={{
                                                                    border: 1,
                                                                    borderColor: "error.light",
                                                                    borderLeft: 3,
                                                                    borderLeftColor: "error.main",
                                                                    borderRadius: 1,
                                                                    px: 1,
                                                                    py: 0.75,
                                                                }}
                                                            >
                                                                <Typography variant="caption" color="text.secondary" sx={{ fontFamily: "Montserrat, sans-serif", display: "block" }}>
                                                                    Reported {formatDate(issue.date)}
                                                                </Typography>
                                                                <Typography variant="body2" sx={{ fontFamily: "Montserrat, sans-serif", fontWeight: 500, mt: 0.25 }}>
                                                                    {issue.description}
                                                                </Typography>
                                                            </Box>
                                                        ))
                                                    )}
                                                </Stack>
                                            </Box>
                                        </>
                                    )}

                                    {/* ── Expired inspections ── */}
                                    {vehicle?.expired_inspections_count > 0 && (
                                        <>
                                            <ButtonBase
                                                onClick={handleToggleInspections}
                                                sx={{
                                                    display: "flex",
                                                    width: "100%",
                                                    alignItems: "center",
                                                    gap: 1,
                                                    borderRadius: 1,
                                                    px: 0.5,
                                                    py: 0.5,
                                                    "&:hover": { bgcolor: "action.hover" },
                                                }}
                                            >
                                                <BuildIcon fontSize="small" color="warning" />
                                                <Typography variant="body2" color="warning.main" sx={{ fontFamily: "Montserrat, sans-serif", fontWeight: 600, flex: 1, textAlign: "left" }}>
                                                    {vehicle.expired_inspections_count} expired {vehicle.expired_inspections_count === 1 ? "inspection" : "inspections"}
                                                </Typography>
                                                <ExpandMoreIcon
                                                    fontSize="small"
                                                    sx={{
                                                        color: "warning.main",
                                                        transition: "transform 0.2s",
                                                        transform: inspectionsOpen ? "rotate(180deg)" : "rotate(0deg)",
                                                    }}
                                                />
                                            </ButtonBase>
                                            <Box sx={{ overflow: "hidden", opacity: inspectionsOpen ? 1 : 0, transform: inspectionsOpen ? "translateY(0)" : "translateY(-4px)", transition: "opacity 0.15s ease, transform 0.15s ease", pointerEvents: inspectionsOpen ? "auto" : "none", display: inspectionsOpen ? undefined : "none" }}>
                                                <Stack spacing={0.75} sx={{ pl: 1, pb: 0.5 }}>
                                                    {inspectionsLoading ? (
                                                        <CircularProgress size={16} color="warning" sx={{ ml: 1 }} />
                                                    ) : expiredInspections.length === 0 ? (
                                                        <Typography variant="caption" color="text.secondary" sx={{ fontFamily: "Montserrat, sans-serif", pl: 0.5 }}>
                                                            No details available.
                                                        </Typography>
                                                    ) : (
                                                        expiredInspections.map((insp) => (
                                                            <Box
                                                                key={insp.id}
                                                                sx={{
                                                                    border: 1,
                                                                    borderColor: "warning.light",
                                                                    borderLeft: 3,
                                                                    borderLeftColor: "warning.main",
                                                                    borderRadius: 1,
                                                                    px: 1,
                                                                    py: 0.75,
                                                                }}
                                                            >
                                                                <Typography variant="caption" color="text.secondary" sx={{ fontFamily: "Montserrat, sans-serif", display: "block" }}>
                                                                    Expired {formatDate(insp.expiration_date)}
                                                                </Typography>
                                                                <Typography variant="body2" sx={{ fontFamily: "Montserrat, sans-serif", fontWeight: 500, mt: 0.25 }}>
                                                                    {insp.description}
                                                                </Typography>
                                                            </Box>
                                                        ))
                                                    )}
                                                </Stack>
                                            </Box>
                                        </>
                                    )}

                                </Stack>
                            </>
                        )}
                    </>
                )}
            </DialogContent>
        </Dialog>
    );
}
